import classNames from 'classnames';
import Image from 'next/image';
import { useCallback, useRef, useState } from 'react';
import { FaCamera, FaFileAlt, FaVideo } from 'react-icons/fa';
import { isProductAvailable } from '@/lib/data-transformer/productData';
import { ProductItem } from '@/lib/types/api';
import useTranslation from 'next-translate/useTranslation';
import { InitDetail } from 'lightgallery/lg-events';
import { ProductGallery } from '../../pages/product/[machine_id]/index';
import { ProductPaymentInfoHero } from './ProductPaymentInfo';
import { upperCase } from 'lodash-es';
import { setIsGetQuoteModalOpen } from '../forms/NewsLetter/modalSlice';
import { useAppDispatch } from '@/redux/hooks';
import { bingUET } from '@/lib/utilities/analytics/bing';
import { pushDataLayer } from '@/lib/utilities/analytics/gtm';

export function ProductHero({ product }: { product: ProductItem }) {
  const image = product.images?.[0];
  const isAvailable = product ? isProductAvailable(product) : false;
  const { t } = useTranslation('products');
  const [shouldLoadSlider, setShouldLoadSlider] = useState(false);
  const [galeryType, setGaleryType] = useState<'photo' | 'video'>('photo');
  const lightGallery = useRef<any>(null);
  const dispatch = useAppDispatch();
  const openGallery = useCallback(() => {
    try {
      lightGallery.current.openGallery();
    } catch (e) {
      console.error(e);
    }
  }, []);

  const onInit = useCallback((detail: InitDetail) => {
    if (detail) {
      lightGallery.current = detail.instance;
      lightGallery.current.openGallery();
    }
  }, []);

  function loadGalery(galeryType?: 'photo' | 'video') {
    if (galeryType) {
      setGaleryType(galeryType);
    }
    if (!shouldLoadSlider) {
      setShouldLoadSlider(true);
    } else {
      openGallery();
    }
  }
  function scrollToData() {
    const data = document.getElementById('data-overview');
    if (data) {
      data.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }

  return (
    <div>
      <section>
        <div className='inner-shaddow-hero relative h-80 sm:h-screen'>
          {shouldLoadSlider && (
            <ProductGallery
              product={product}
              onInit={onInit}
              galleryType={galeryType}
            />
          )}
          {image && (
            <>
              <div className='no-print'>
                <Image
                  src={image?.image_url}
                  layout='fill'
                  objectFit='cover'
                  alt={image?.alt}
                  priority={true}
                  onClick={() => loadGalery()}
                  className='cursor-pointer'
                />
              </div>
              <div className='print-image'>
                <Image
                  src={image?.image_url}
                  layout='fill'
                  objectFit='contain'
                  alt={image?.alt}
                />
              </div>
            </>
          )}
          <div className='no-print absolute bottom-8 left-12 z-10 flex flex-col'>
            <p className='h1 hidden pb-4 font-extralight text-white sm:inline-block'>
              <strong>{product.brand.name}</strong> {product.model}
            </p>
            <div className='flex space-x-4'>
              {product?.images && product?.images?.length > 0 && (
                <div
                  className='z-10 hidden cursor-pointer rounded bg-slate-600 px-14 py-2 text-center uppercase text-white lg:inline-block'
                  onClick={() => loadGalery('photo')}
                >
                  {t('products:DETAIL_PAGE_PHOTO_GALLERY')}
                </div>
              )}

              {product?.videos && product?.videos?.length > 0 && (
                <div
                  className='z-10 hidden cursor-pointer rounded bg-slate-600 px-14 py-2 text-center uppercase text-white lg:inline-block'
                  onClick={() => loadGalery('video')}
                >
                  {t('products:DETAIL_PAGE_VIDEO_GALLERY')}
                </div>
              )}
            </div>
            <div className='flex space-x-12'>
              {product?.images && product?.images?.length > 0 && (
                <FaCamera
                  className='h-8 w-8 cursor-pointer text-white lg:hidden'
                  onClick={() => loadGalery('photo')}
                />
              )}
              <FaFileAlt
                className='h-8 w-8 text-white lg:hidden'
                onClick={scrollToData}
              />
              {product?.videos && product?.videos?.length > 0 && (
                <FaVideo
                  className='h-8 w-8 cursor-pointer text-white lg:hidden'
                  onClick={() => loadGalery('video')}
                />
              )}
            </div>
          </div>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src='/arrow-down2.svg'
            alt='An SVG of an eye'
            className='absolute bottom-0 left-1/2 z-20 hidden h-16 w-24 -translate-x-1/2 transform animate-pulse text-white md:block'
          />
          <div
            className={
              'no-print absolute bottom-8 right-12 z-10 hidden font-bold uppercase !text-white md:inline-block'
            }
          >
            <ProductPaymentInfoHero
              product={product}
              priceClass='text-white font-bold h2'
            />

            <div
              className={classNames({
                'z-10 mt-4 hidden cursor-pointer rounded px-14 py-2 font-bold uppercase text-white md:inline-block':
                  true,
                'bg-zinc-400': !isAvailable,
                'bg-success-500': isAvailable,
              })}
              onClick={() => {
                if (isAvailable) {
                  dispatch(setIsGetQuoteModalOpen(true));
                  pushDataLayer('formInit', {
                    formType: 'Get a quote',
                  });
                  bingUET('Get an official quote');
                }
              }}
            >
              {isAvailable
                ? t('products:DETAIL_MODALQUOTE_HEADER')
                : upperCase(t('products:Sold'))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
